/**
 * Typované schéma konfiguračních polí per konektor — port legacy
 * `connector-config.js` `connectorFields`. Používá ho typovaný editační
 * formulář ConnectorsPage místo raw JSON textarea.
 *
 * Mapování na payload (mirror legacy saveConfig):
 * - `config` pole → `config[key]`
 * - `nested[section].fields` → `config[section][key]`
 * - `nested[section].credentials` + top `credentials` → `credentials[key]` (flat)
 */

export type ConnFieldType = "text" | "number" | "checkbox" | "password" | "select";

export interface ConnSelectOption {
  value: string;
  label: string;
}

export interface ConnField {
  key: string;
  label: string;
  type: ConnFieldType;
  hint?: string;
  /** Options for `type: "select"`. */
  options?: ConnSelectOption[];
}

/** Sdílený hint u pole TLS Versions — prázdné = dědí globální TLS. */
const TLS_VERSIONS_FIELD: ConnField = {
  key: "tlsVersions",
  label: "TLS Versions",
  type: "text",
  hint: "Prázdné = dědí globální TLS (Nastavení → Obecné → HTTP_TLS_VERSIONS).",
};

export interface ConnNestedSection {
  title: string;
  /** Short label for the section's tab (falls back to `title`). */
  tabLabel?: string;
  fields: ConnField[];
  credentials?: ConnField[];
}

export interface ConnSchema {
  config: ConnField[];
  nested: Record<string, ConnNestedSection>;
  credentials: ConnField[];
}

export const CONNECTOR_FIELDS: Record<string, ConnSchema> = {
  ECOBRA: {
    config: [
      { key: "baseUrl", label: "Base URL", type: "text" },
      { key: "connectionTimeout", label: "Connection Timeout (ms)", type: "number" },
      { key: "readTimeout", label: "Read Timeout (ms)", type: "number" },
      { key: "callerSystem", label: "Caller System ID", type: "number" },
    ],
    nested: {
      retry: {
        title: "Retry Configuration",
        tabLabel: "Retry",
        fields: [
          { key: "maxAttempts", label: "Max Attempts", type: "number" },
          { key: "delayMs", label: "Delay (ms)", type: "number" },
        ],
      },
      mtls: {
        title: "mTLS (client certificate for F5 / API gateway)",
        tabLabel: "mTLS",
        fields: [
          { key: "enabled", label: "Enabled", type: "checkbox" },
          { key: "keystorePath", label: "Keystore Path (e.g. file:/app/certs/client.p12)", type: "text" },
          { key: "keystoreType", label: "Keystore Type (PKCS12 / JKS)", type: "text" },
          { key: "truststorePath", label: "Truststore Path (optional)", type: "text" },
          { key: "truststoreType", label: "Truststore Type (JKS / PKCS12)", type: "text" },
        ],
        credentials: [
          { key: "keystorePassword", label: "Keystore Password", type: "password" },
          { key: "truststorePassword", label: "Truststore Password", type: "password" },
        ],
      },
      tls: {
        title: "TLS (protocol versions)",
        tabLabel: "TLS",
        fields: [TLS_VERSIONS_FIELD],
      },
    },
    credentials: [],
  },
  CSOB_ECHANNELS: {
    config: [
      { key: "connectionTimeout", label: "Connection Timeout (ms)", type: "number" },
      { key: "readTimeout", label: "Read Timeout (ms)", type: "number" },
    ],
    nested: {
      oauth2: {
        title: "OAuth2 Settings",
        tabLabel: "OAuth2",
        fields: [
          { key: "tokenUrl", label: "Token URL", type: "text" },
          { key: "grantType", label: "Grant Type", type: "text" },
          { key: "clientId", label: "Client ID", type: "text" },
          { key: "clientSecret", label: "Client Secret", type: "password" },
        ],
      },
      activationApi: {
        title: "Activation API",
        tabLabel: "Activation",
        fields: [
          { key: "baseUrl", label: "Base URL", type: "text" },
          { key: "scope", label: "OAuth2 Scope", type: "text" },
        ],
      },
      signingApi: {
        title: "Signing API",
        tabLabel: "Signing",
        fields: [
          { key: "baseUrl", label: "Base URL", type: "text" },
          { key: "scope", label: "OAuth2 Scope", type: "text" },
        ],
      },
      spbApi: {
        title: "SPB API (Optional)",
        tabLabel: "SPB",
        fields: [
          { key: "enabled", label: "Enabled", type: "checkbox" },
          { key: "baseUrl", label: "Base URL", type: "text" },
          { key: "apiKeyHeader1", label: "API Key Header 1", type: "text" },
          { key: "apiKeyHeader2", label: "API Key Header 2", type: "text" },
          { key: "apiKey1", label: "API Key 1", type: "password" },
          { key: "apiKey2", label: "API Key 2", type: "password" },
        ],
      },
      tls: {
        title: "TLS (protocol versions)",
        tabLabel: "TLS",
        fields: [TLS_VERSIONS_FIELD],
      },
    },
    credentials: [],
  },
  ECOBRA_SOAP: {
    config: [
      { key: "endpoint", label: "SOAP Endpoint URL", type: "text" },
      { key: "connectionTimeout", label: "Connection Timeout (ms)", type: "number" },
      { key: "readTimeout", label: "Read Timeout (ms)", type: "number" },
    ],
    nested: {
      defaults: {
        title: "Default Values",
        tabLabel: "Defaults",
        fields: [
          { key: "clientIdent", label: "Client Identification", type: "text" },
          { key: "system", label: "System ID", type: "number" },
        ],
      },
      mtls: {
        title: "mTLS Configuration",
        tabLabel: "mTLS",
        fields: [
          { key: "enabled", label: "Enabled", type: "checkbox" },
          { key: "keystorePath", label: "Keystore Path", type: "text" },
          { key: "keystoreType", label: "Keystore Type", type: "text" },
          { key: "truststorePath", label: "Truststore Path", type: "text" },
          { key: "truststoreType", label: "Truststore Type", type: "text" },
        ],
        credentials: [
          { key: "keystorePassword", label: "Keystore Password", type: "password" },
          { key: "truststorePassword", label: "Truststore Password", type: "password" },
        ],
      },
      tls: {
        title: "TLS (protocol versions)",
        tabLabel: "TLS",
        fields: [TLS_VERSIONS_FIELD],
      },
    },
    credentials: [],
  },
  KB_CAAS: {
    config: [
      { key: "baseUrl", label: "Base URL", type: "text" },
      { key: "connectionTimeout", label: "Connection Timeout (ms)", type: "number" },
      { key: "readTimeout", label: "Read Timeout (ms)", type: "number" },
      { key: "realm", label: "Realm", type: "text" },
      { key: "environment", label: "Environment (X-bff-environment)", type: "text" },
      { key: "defaultPlatform", label: "Default Platform (X-Kb-Fe-Platform)", type: "text" },
      { key: "secStorageVersion", label: "Secure Storage Version (initContext)", type: "text" },
      { key: "rosBaseUrl", label: "ROS Base URL (optional — derived from Base URL when blank)", type: "text" },
    ],
    nested: {
      tls: {
        title: "TLS (protocol versions)",
        tabLabel: "TLS",
        fields: [TLS_VERSIONS_FIELD],
      },
      proxy: {
        title: "HTTP Proxy",
        tabLabel: "Proxy",
        fields: [
          {
            key: "mode",
            label: "Proxy Mode",
            type: "select",
            options: [
              { value: "auto", label: "Auto (use proxy when host is set)" },
              { value: "off", label: "Off (direct — ignore proxy)" },
              { value: "on", label: "On (force proxy)" },
            ],
            hint: "Off = jde napřímo na gateway a ignoruje i systémovou proxy (HTTPS_PROXY). On = vyžaduje vyplněný host.",
          },
          { key: "host", label: "Proxy Host", type: "text" },
          { key: "port", label: "Proxy Port", type: "number" },
          {
            key: "authScheme",
            label: "Proxy Auth Scheme",
            type: "select",
            options: [
              { value: "", label: "Auto (NTLM when username set, else none)" },
              { value: "none", label: "None" },
              { value: "ntlm", label: "NTLM (username/password, DOMAIN\\user)" },
              { value: "negotiate", label: "Negotiate / Kerberos (SPNEGO)" },
            ],
            hint: "KB proxy nabízí 'Negotiate, NTLM'. Negotiate vyžaduje Kerberos konfiguraci JVM (krb5.conf + ticket/keytab).",
          },
          { key: "sslVerificationDisabled", label: "Skip SSL Verification", type: "checkbox" },
        ],
      },
      proxyEndpoints: {
        title: "Proxy Endpoints",
        tabLabel: "Endpoints",
        fields: [
          {
            key: "route",
            label: "Routing",
            type: "select",
            options: [
              { value: "inherit", label: "Inherit (stejně jako gateway)" },
              { value: "direct", label: "Direct (vždy napřímo)" },
              { value: "proxy", label: "Proxy (vždy přes proxy)" },
            ],
            hint: "Nezávislé na Proxy Mode: gateway může být dostupná napřímo, zatímco OTP test access jen přes proxy (nebo naopak).",
          },
          { key: "otptestaccess", label: "OTP Test Access URL", type: "text" },
        ],
      },
      appConfigNdbSdk: {
        title: "App Config — NDB_SDK (Moje KB)",
        tabLabel: "NDB_SDK",
        fields: [
          { key: "clientId", label: "Client ID", type: "text" },
          { key: "serviceProvider", label: "Service Provider", type: "text" },
          { key: "activationSpec", label: "Activation Spec", type: "text" },
          { key: "redirectUri", label: "OAuth Redirect URI", type: "text" },
          { key: "loginScopes", label: "Login Scopes (comma-separated)", type: "text" },
          { key: "authorizeScopes", label: "Authorize Scopes (comma-separated)", type: "text" },
        ],
      },
      appConfigStandalone: {
        title: "App Config — STANDALONE (KB Klíč)",
        tabLabel: "STANDALONE",
        fields: [
          { key: "clientId", label: "Client ID", type: "text" },
          { key: "serviceProvider", label: "Service Provider", type: "text" },
          { key: "activationSpec", label: "Activation Spec", type: "text" },
          { key: "redirectUri", label: "OAuth Redirect URI", type: "text" },
          { key: "loginScopes", label: "Login Scopes (comma-separated)", type: "text" },
          { key: "authorizeScopes", label: "Authorize Scopes (comma-separated)", type: "text" },
        ],
      },
    },
    credentials: [
      { key: "rosUsername", label: "ROS Username (service account for transaction /auth/ros)", type: "text" },
      { key: "rosPassword", label: "ROS Password (service account for transaction /auth/ros)", type: "password" },
      { key: "username", label: "Username (Basic Auth — unused by KB CAAS)", type: "text" },
      { key: "password", label: "Password (Basic Auth — unused by KB CAAS)", type: "password" },
      { key: "proxyUsername", label: "Proxy Username", type: "text" },
      { key: "proxyPassword", label: "Proxy Password", type: "password" },
    ],
  },
  PRIVAT: {
    config: [
      { key: "baseUrl", label: "Base URL", type: "text" },
      { key: "connectionTimeout", label: "Connection Timeout (ms)", type: "number" },
      { key: "readTimeout", label: "Read Timeout (ms)", type: "number" },
      { key: "sslVerificationDisabled", label: "Skip SSL Verification", type: "checkbox" },
      { key: "autoLoginApiTools", label: "Auto-login (ApiTools)", type: "checkbox" },
      {
        key: "autoLoginApi",
        label: "Auto-login (zákaznické REST API)",
        type: "checkbox",
        hint:
          "Vypnuto (výchozí) = volání na /api/v1/** si Bearer token dodávají sama hlavičkou " +
          "X-etts-Authorization. Zapnuto = server si ho obstará sám; usnadní to psaní skriptů, " +
          "ale přihlašuje se jménem vlastníka tokenu. Ručně dodaná hlavička má vždy přednost.",
      },
    ],
    nested: {
      tls: {
        title: "TLS (protocol versions)",
        tabLabel: "TLS",
        fields: [TLS_VERSIONS_FIELD],
      },
    },
    credentials: [],
  },
};
